import { PropsWithChildren, ReactNode, useMemo } from "react";
import { TrendingDownIcon, TrendingUpIcon } from "lucide-react";
import {
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { RatioFormatter } from "@/v2/components/formatters/RationFormatter";
import { Badge } from "@/components/ui/badge";
import { DateRange } from "@/components/shared/date-range-picker/DateRangePicker";
import { useTrend } from "@/v2/shared/hooks/useTrend";
import {
  ExploreInteractionType,
  exploreInteractionTypeToLabel,
} from "@/v2/entities/explore/Explore.model";
import { Filter, mergeFilter } from "@/v2/entities/explore/Explore.filter";
import {
  buildParams,
  ExploreInteractionValueDistributionResponse,
  useExploreApi,
} from "@/v2/entities/explore/Explore.api";
import { AvgQaScoreFormatter } from "@/v2/components/formatters/AvgQaScoreFormatter";
import { Tooltip } from "@/components/shared/tooltip";
import { Flex, IconButton, Text } from "@radix-ui/themes";
import { InfoCircledIcon } from "@radix-ui/react-icons";
import { CSATFormatter } from "@/v2/components/formatters/CSATFormatter";
import { TimeToFirstResponseFormatter } from "@/v2/entities/explore/formatters/TimeToFirstResponseFormatter";
import { TimeToResolutionFormatter } from "@/v2/entities/explore/formatters/TimeToResolutionFormatter";
import { exploreAggColumnTypeToLabel } from "@/v2/entities/explore/columns/ExploreColum.model";

function isValidNumberForDivision(count: number): boolean {
  return count != null && count !== 0 && !isNaN(count);
}

function useCountTrend(
  source: ExploreInteractionType,
  dateRange: DateRange,
  baseFilter?: Filter
) {
  const { getInteractionsCount } = useExploreApi();

  return useTrend({
    dateRange,
    queryKey: ["explore-v2", "count", source],
    queryFn: getInteractionsCount,
    getParams: (window) => {
      const windowFilter: Filter = {
        and: [
          {
            createdAt: {
              gte: window.dateFrom,
            },
          },
          {
            createdAt: {
              lte: window.dateTo,
            },
          },
        ],
      };

      const filter =
        baseFilter != null
          ? mergeFilter(baseFilter, windowFilter)
          : windowFilter;

      const params = buildParams({
        source,
        filter,
      });

      return params.length > 0 ? `?${params.toString()}` : "";
    },
    calculateTrend: (results) => {
      const firstCount = results[results.length - 1]?.response?.count;
      const lastCount = results[0]?.response?.count;

      if (
        !isValidNumberForDivision(firstCount) ||
        !isValidNumberForDivision(lastCount)
      ) {
        return;
      }

      return (lastCount - firstCount) / firstCount;
    },
  });
}

type QuackResolved = {
  total: number;
  quackResolved: number;
};

function calculateQuackResolved(
  response: ExploreInteractionValueDistributionResponse
): QuackResolved & { rate: number } {
  const { total, quackResolved } = response.results.reduce<QuackResolved>(
    (acc, result) => {
      const resolved = Number(result["RESOLVED"] ?? 0);
      const expectedEscalation = Number(result["EXPECTED_ESCALATION"] ?? 0);
      const unexpectedEscalation = Number(result["UNEXPECTED_ESCALATION"] ?? 0);

      const quackResolved = resolved + expectedEscalation;
      const totalCount = resolved + expectedEscalation + unexpectedEscalation;

      acc.total += totalCount;
      acc.quackResolved += quackResolved;

      return acc;
    },
    { total: 0, quackResolved: 0 }
  );

  return {
    total,
    quackResolved,
    rate: quackResolved / total,
  };
}

function useQuackSuccessCoverageTrend(
  source: ExploreInteractionType,
  dateRange: DateRange,
  baseFilter?: Filter
) {
  const { getInteractionValueDistribution } = useExploreApi();

  const { trend, response } = useTrend({
    dateRange,
    queryKey: ["explore-v2", "quack-success-coverage", source],
    queryFn: getInteractionValueDistribution,
    getParams: (window) => {
      const windowFilter: Filter = {
        and: [
          {
            createdAt: {
              gte: window.dateFrom,
            },
          },
          {
            createdAt: {
              lte: window.dateTo,
            },
          },
        ],
      };

      const filter =
        baseFilter != null
          ? mergeFilter(baseFilter, windowFilter)
          : windowFilter;

      const params = buildParams({
        source,
        filter,
        groupBy: "tenantId",
        column: "resolutionStatus",
      });

      return params.length > 0 ? `?${params.toString()}` : "";
    },
    calculateTrend: (results) => {
      const first = results[results.length - 1]?.response;
      const last = results[0]?.response;

      if (first == null || last == null) {
        return;
      }

      const { rate: firstRate } = calculateQuackResolved(first);
      const { rate: lastRate } = calculateQuackResolved(last);

      return lastRate - firstRate;
    },
  });

  return useMemo(() => {
    if (response == null) {
      return { trend: undefined, rate: undefined };
    }

    const { rate } = calculateQuackResolved(response);

    return {
      trend,
      rate,
    };
  }, [trend, response]);
}

function useAvgQaScoreTrend(
  source: ExploreInteractionType,
  dateRange: DateRange,
  baseFilter?: Filter
) {
  const { getAggregatedInteractions } = useExploreApi();

  return useTrend({
    dateRange,
    queryKey: ["explore-v2", "avg-qa-score", source],
    queryFn: getAggregatedInteractions,
    getParams: (window) => {
      const windowFilter: Filter = {
        and: [
          {
            createdAt: {
              gte: window.dateFrom,
            },
          },
          {
            createdAt: {
              lte: window.dateTo,
            },
          },
        ],
      };

      const filter =
        baseFilter != null
          ? mergeFilter(baseFilter, windowFilter)
          : windowFilter;

      const params = buildParams({
        source,
        filter,
        groupBy: "tenantId",
      });

      return params.length > 0 ? `?${params.toString()}` : "";
    },
    calculateTrend: (results) => {
      const firstAvgQaScore =
        results[results.length - 1]?.response.results[0]?.avgQaScore;
      const lastAvgQaScore = results[0]?.response.results[0]?.avgQaScore;

      if (firstAvgQaScore == null || lastAvgQaScore == null) {
        return;
      }

      return lastAvgQaScore - firstAvgQaScore;
    },
  });
}

function useAvgCsatTrend(
  source: ExploreInteractionType,
  dateRange: DateRange,
  baseFilter?: Filter
) {
  const { getAggregatedInteractions } = useExploreApi();

  return useTrend({
    dateRange,
    queryKey: ["explore-v2", "avg-csat", source],
    queryFn: getAggregatedInteractions,
    getParams: (window) => {
      const windowFilter: Filter = {
        and: [
          {
            createdAt: {
              gte: window.dateFrom,
            },
          },
          {
            createdAt: {
              lte: window.dateTo,
            },
          },
        ],
      };

      const filter =
        baseFilter != null
          ? mergeFilter(baseFilter, windowFilter)
          : windowFilter;

      const params = buildParams({
        source,
        filter,
        groupBy: "tenantId",
      });

      return params.length > 0 ? `?${params.toString()}` : "";
    },
    calculateTrend: (results) => {
      const firstAvgCsat =
        results[results.length - 1]?.response.results[0]?.avgCsat;
      const lastAvgCsat = results[0]?.response.results[0]?.avgCsat;

      if (firstAvgCsat == null || lastAvgCsat == null) {
        return;
      }

      return lastAvgCsat - firstAvgCsat;
    },
  });
}

function useAvgTimeToResolutionTrend(
  source: ExploreInteractionType,
  dateRange: DateRange,
  baseFilter?: Filter
) {
  const { getAggregatedInteractions } = useExploreApi();

  return useTrend({
    dateRange,
    queryKey: ["explore-v2", "avg-time-to-resolution", source],
    queryFn: getAggregatedInteractions,
    getParams: (window) => {
      const windowFilter: Filter = {
        and: [
          {
            createdAt: {
              gte: window.dateFrom,
            },
          },
          {
            createdAt: {
              lte: window.dateTo,
            },
          },
        ],
      };

      const filter =
        baseFilter != null
          ? mergeFilter(baseFilter, windowFilter)
          : windowFilter;

      const params = buildParams({
        source,
        filter: windowFilter,
        groupBy: "tenantId",
      });

      return params.length > 0 ? `?${params.toString()}` : "";
    },
    calculateTrend: (results) => {
      const firstAvgTimeToResolution =
        results[results.length - 1]?.response.results[0]?.avgTimeToResolution;
      const lastAvgTimeToResolution =
        results[0]?.response.results[0]?.avgTimeToResolution;

      if (firstAvgTimeToResolution == null || lastAvgTimeToResolution == null) {
        return;
      }

      return lastAvgTimeToResolution - firstAvgTimeToResolution;
    },
  });
}

function useAvgTimeToFirstResponseTrend(
  source: ExploreInteractionType,
  dateRange: DateRange,
  baseFilter?: Filter
) {
  const { getAggregatedInteractions } = useExploreApi();

  return useTrend({
    dateRange,
    queryKey: ["explore-v2", "avg-time-to-first-response", source],
    queryFn: getAggregatedInteractions,
    getParams: (window) => {
      const windowFilter: Filter = {
        and: [
          {
            createdAt: {
              gte: window.dateFrom,
            },
          },
          {
            createdAt: {
              lte: window.dateTo,
            },
          },
        ],
      };

      const filter =
        baseFilter != null
          ? mergeFilter(baseFilter, windowFilter)
          : windowFilter;

      const params = buildParams({
        source,
        filter,
        groupBy: "tenantId",
      });

      return params.length > 0 ? `?${params.toString()}` : "";
    },
    calculateTrend: (results) => {
      const firstAvgTimeToFirstResponse =
        results[results.length - 1]?.response.results[0]
          ?.avgTimeToFirstResponse;
      const lastAvgTimeToFirstResponse =
        results[0]?.response.results[0]?.avgTimeToFirstResponse;

      if (
        firstAvgTimeToFirstResponse == null ||
        lastAvgTimeToFirstResponse == null
      ) {
        return;
      }

      return lastAvgTimeToFirstResponse - firstAvgTimeToFirstResponse;
    },
  });
}

interface TrendMetricTooltipProps {
  title: string;
  description: ReactNode;
}

export function TrendMetricTooltip({
  title,
  description,
}: TrendMetricTooltipProps) {
  return (
    <Flex p="4" direction="column" gap="2" maxWidth="280px">
      <Text size="3" weight="medium" highContrast>
        {title}
      </Text>

      <Text size="2">{description}</Text>
    </Flex>
  );
}

interface TrendMetricCardProps {
  title: string;
  trend?: number;
  tooltip?: ReactNode;
}

export function TrendMetricCard({
  title,
  trend,
  tooltip,
  children,
}: PropsWithChildren<TrendMetricCardProps>) {
  return (
    <Card className="@container/card">
      <CardHeader className="relative">
        <CardDescription className="flex items-center gap-2">
          <Text truncate className="flex-1">
            {title}
          </Text>

          {tooltip != null && (
            <Tooltip>
              <Tooltip.Trigger>
                <IconButton variant="soft" size="2" color="gray">
                  <InfoCircledIcon height={16} width={16} />
                </IconButton>
              </Tooltip.Trigger>

              <Tooltip.Content align="center">{tooltip}</Tooltip.Content>
            </Tooltip>
          )}

          {trend != null && (
            <Badge variant="outline" className="flex gap-1 rounded-lg text-xs">
              {trend > 0 ? (
                <TrendingUpIcon className="size-3" />
              ) : (
                <TrendingDownIcon className="size-3" />
              )}

              <RatioFormatter value={trend} />
            </Badge>
          )}
        </CardDescription>

        <CardTitle className="@[250px]/card:text-3xl text-3xl font-semibold tabular-nums">
          {children}
        </CardTitle>
      </CardHeader>

      <CardFooter className="flex-col items-start gap-1 text-sm">
        <div className="line-clamp-1 flex gap-2 font-medium">
          {trend != null && (
            <>
              {`Trending ${trend > 0 ? "up" : "down"} this period`}

              {trend > 0 ? (
                <TrendingUpIcon className="size-4" />
              ) : (
                <TrendingDownIcon className="size-4" />
              )}
            </>
          )}
        </div>
      </CardFooter>
    </Card>
  );
}

interface TrendMetricsProps {
  source: ExploreInteractionType;
  dateRange: DateRange;
  filter?: Filter;
}

function CountMetricTooltip({ source }: { source: ExploreInteractionType }) {
  return (
    <TrendMetricTooltip
      title={`${exploreInteractionTypeToLabel[source].plural}`}
      description={`Total ${exploreInteractionTypeToLabel[
        source
      ].plural.toLowerCase()} created in the selected date range.`}
    />
  );
}

function QuackSuccessCoverageTooltip({
  source,
}: {
  source: ExploreInteractionType;
}) {
  return (
    <TrendMetricTooltip
      title="Quack Handled"
      description={`% of Quack-handled ${exploreInteractionTypeToLabel[
        source
      ].plural.toLowerCase()} resolved or escalated as expected, compared to the previous period.`}
    />
  );
}

function AvgQaScoreTooltip({ source }: { source: ExploreInteractionType }) {
  return (
    <TrendMetricTooltip
      title={exploreAggColumnTypeToLabel["avgQaScore"]}
      description={`Average QA score across all evaluated ${exploreInteractionTypeToLabel[
        source
      ].plural.toLowerCase()} (Quack + human) in the selected period, compared to the previous period.`}
    />
  );
}

export function PerformanceTrendMetrics({
  source,
  dateRange,
  filter,
}: TrendMetricsProps) {
  const countTrend = useCountTrend(source, dateRange, filter);
  const quackSuccessCoverageTrend = useQuackSuccessCoverageTrend(
    source,
    dateRange,
    filter
  );
  const avgQaScoreTrend = useAvgQaScoreTrend(source, dateRange, filter);

  return (
    <div className="*:data-[slot=card]:shadow-xs @xl/main:grid-cols-3 @5xl/main:grid-cols-3 grid grid-cols-3 gap-3 *:data-[slot=card]:bg-gradient-to-t *:data-[slot=card]:from-primary/5 *:data-[slot=card]:to-card dark:*:data-[slot=card]:bg-card">
      <TrendMetricCard
        title={exploreInteractionTypeToLabel[source].plural}
        trend={countTrend.trend}
        tooltip={<CountMetricTooltip source={source} />}
      >
        {countTrend.response?.count?.toLocaleString() ?? "-"}
      </TrendMetricCard>

      <TrendMetricCard
        title="Quack Handled"
        trend={quackSuccessCoverageTrend.trend}
        tooltip={<QuackSuccessCoverageTooltip source={source} />}
      >
        {quackSuccessCoverageTrend.rate != null &&
        !isNaN(quackSuccessCoverageTrend.rate) ? (
          <RatioFormatter value={quackSuccessCoverageTrend.rate} />
        ) : (
          <div className="text-muted-foreground">-</div>
        )}
      </TrendMetricCard>

      <TrendMetricCard
        title={exploreAggColumnTypeToLabel["avgQaScore"]}
        trend={avgQaScoreTrend.trend}
        tooltip={<AvgQaScoreTooltip source={source} />}
      >
        {avgQaScoreTrend.response?.results[0]?.avgQaScore != null &&
        !isNaN(avgQaScoreTrend.response?.results[0]?.avgQaScore) ? (
          <AvgQaScoreFormatter
            value={avgQaScoreTrend.response?.results[0]?.avgQaScore}
          />
        ) : (
          "-"
        )}
      </TrendMetricCard>
    </div>
  );
}

function AvgCsatTooltip() {
  return (
    <TrendMetricTooltip
      title={exploreAggColumnTypeToLabel["avgCsat"]}
      description={`Average customer satisfaction (CSAT) score from all rated tickets in the selected period,`}
    />
  );
}

function AvgTimeToResolutionTooltip() {
  return (
    <TrendMetricTooltip
      title={exploreAggColumnTypeToLabel["avgTimeToResolution"]}
      description={`Average time to resolve tickets in the selected period, compared to the previous period.`}
    />
  );
}

function AvgTimeToFirstResponseTooltip() {
  return (
    <TrendMetricTooltip
      title={exploreAggColumnTypeToLabel["avgTimeToFirstResponse"]}
      description={`Average first time to response in the selected period, compared to the previous period.`}
    />
  );
}

export function QualityTrendMetrics({ source, dateRange }: TrendMetricsProps) {
  const avgCsatTrend = useAvgCsatTrend(source, dateRange);
  const avgTimeToResolutionTrend = useAvgTimeToResolutionTrend(
    source,
    dateRange
  );
  const avgTimeToFirstResponseTrend = useAvgTimeToFirstResponseTrend(
    source,
    dateRange
  );

  return (
    <div className="*:data-[slot=card]:shadow-xs @xl/main:grid-cols-3 @5xl/main:grid-cols-3 grid grid-cols-3 gap-3 *:data-[slot=card]:bg-gradient-to-t *:data-[slot=card]:from-primary/5 *:data-[slot=card]:to-card dark:*:data-[slot=card]:bg-card">
      <TrendMetricCard
        title={exploreAggColumnTypeToLabel["avgCsat"]}
        tooltip={<AvgCsatTooltip />}
      >
        {avgCsatTrend.response?.results[0]?.avgCsat != null &&
        !isNaN(avgCsatTrend.response?.results[0]?.avgCsat) ? (
          <CSATFormatter value={avgCsatTrend.response?.results[0]?.avgCsat} />
        ) : (
          "-"
        )}
      </TrendMetricCard>

      <TrendMetricCard
        title={exploreAggColumnTypeToLabel["avgTimeToResolution"]}
        tooltip={<AvgTimeToResolutionTooltip />}
      >
        {avgTimeToResolutionTrend.response?.results[0]?.avgTimeToResolution !=
          null &&
        !isNaN(
          avgTimeToResolutionTrend.response?.results[0]?.avgTimeToResolution
        ) ? (
          <TimeToResolutionFormatter
            shorthand
            value={
              avgTimeToResolutionTrend.response?.results[0]?.avgTimeToResolution
            }
          />
        ) : (
          "-"
        )}
      </TrendMetricCard>

      <TrendMetricCard
        title={exploreAggColumnTypeToLabel["avgTimeToFirstResponse"]}
        tooltip={<AvgTimeToFirstResponseTooltip />}
      >
        {avgTimeToFirstResponseTrend.response?.results[0]
          ?.avgTimeToFirstResponse != null &&
        !isNaN(
          avgTimeToFirstResponseTrend.response?.results[0]
            ?.avgTimeToFirstResponse
        ) ? (
          <TimeToFirstResponseFormatter
            shorthand
            value={
              avgTimeToFirstResponseTrend.response?.results[0]
                ?.avgTimeToFirstResponse
            }
          />
        ) : (
          "-"
        )}
      </TrendMetricCard>
    </div>
  );
}
